import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ReportTheme } from '../../types';
import { getChartColor } from './chartUtils';

interface NewVsReturningUsersBarChartWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const NewVsReturningUsersBarChartWidget: React.FC<NewVsReturningUsersBarChartWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {

  // Process the raw API data internally for New vs Returning Users
  const processData = () => {
    if (!data || !data.data || typeof data.data !== 'object') {
      console.warn('NewVsReturningUsersBarChartWidget: Invalid data structure');
      return null;
    }

    const rawData = data.data;
    const entries = Object.entries(rawData);
    
    if (entries.length === 0) {
      console.warn('NewVsReturningUsersBarChartWidget: No data entries found');
      return null;
    }

    const firstEntry = entries[0];
    const [, firstDayData] = firstEntry;
    
    if (!firstDayData || typeof firstDayData !== 'object' || !(firstDayData as any).date) {
      console.warn('NewVsReturningUsersBarChartWidget: Invalid data format');
      return null;
    }

    if (typeof (firstDayData as any).new_users !== 'number' || typeof (firstDayData as any).users !== 'number') {
      console.warn('NewVsReturningUsersBarChartWidget: Missing required metrics (new_users, users)');
      return null;
    }
    
    const chartData = entries.map(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object' || !dayData.date) {
        return null;
      }

      const returningUsers = (dayData.users || 0) - (dayData.new_users || 0);

      return {
        name: new Date(dayData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        new_users: dayData.new_users || 0,
        returning_users: Math.max(0, returningUsers)
      };
    }).filter((item): item is { name: string; new_users: number; returning_users: number } => item !== null)
      .sort((a, b) => new Date(a.name).getTime() - new Date(b.name).getTime());

    return chartData.length > 0 ? chartData : null;
  };

  const processedData = processData();

  if (!processedData || processedData.length === 0) {
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={processedData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Bar 
          dataKey="new_users" 
          fill={getChartColor(0, reportTheme.theme)} 
          name="New Users"
        />
        <Bar 
          dataKey="returning_users" 
          fill={getChartColor(1, reportTheme.theme)} 
          name="Returning Users"
        />
      </BarChart>
    </ResponsiveContainer>
  );
};
